import React, { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import classNames from 'classnames';
import qs from 'qs';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

import TableBodyCheckbox from '../../../../../components/Table/BodyCheck';
import Dropdown from '../../../../../components/Dropdown/Default';
import Tooltip from '../../../../../components/Tooltip';

import { makeTableRow, generateAccessItemDrop, generateAccessMassDrop, checkTableCount, itemDropStatuses } from '../../helpers';
import { isValidURL } from '../../../../../utils/helper';
import { useTbodyPosition } from '../../../../../utils/hooks/useTbodyPosition';
import { getAccessRoutes } from '../../../../../routers/helpers';

const TableBody = ({ isLoading, tasks, values, onChangeCheck, handleSelect, access, config }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const queryString = qs.parse(location.search.slice(1));
  const fullNavbarRoutes = getAccessRoutes();

  const bodyRef = useRef(null);
  const position = useTbodyPosition({ bodyRef, count: tasks?.length, bodyHeight: '300px' });

  return (
    <tbody
      className={classNames('', { 'tbody-loading': isLoading })}
      style={{ backgroundPositionY: position.y, backgroundPositionX: position.x }}
      ref={bodyRef}
    >
      {tasks?.map((item, index) => (
        <Fragment key={index}>
          <tr key={index} className={values.includes(item.id) ? 'active' : ''}>
            {makeTableRow(item).map((elem, index) => {
              if (elem.type === 'checkbox') {
                if (generateAccessMassDrop(access, t, queryString).length === 0) {
                  return null;
                }

                return (
                  <TableBodyCheckbox
                    key={index}
                    rowSpan={2}
                    item={item}
                    values={values}
                    disabled={item.status === 4 || item.status === 6 || item.status === 7 || item.status === 3 || item.status === 2}
                    onChangeCheck={onChangeCheck}
                  />
                );
              }

              if (elem.type === 'actions') {
                const itemDrops = generateAccessItemDrop(item?.access, access, t);

                if (itemDrops.length === 0) {
                  return (
                    <td key={index} className="td-caret" rowSpan="2">
                      {' '}
                    </td>
                  );
                }

                return (
                  <td key={index} className="td-caret" rowSpan="2">
                    <Dropdown
                      handleSelect={(key, title) => handleSelect({ key, title, id: item.id }, 'single')}
                      size="xsmall"
                      title={t('Table.Actions')}
                      items={itemDrops}
                      forMassActions
                      classForBtnDrop="font__weight-med"
                      dropStyle="default"
                      toolbarClass="dropdown-right"
                      submenuStatuses={itemDropStatuses(config?.statuses, item.access.change_status, t)}
                      submenuPlacement="left"
                      user={item}
                      id={item.id}
                      item={item}
                    />
                  </td>
                );
              }

              if (elem.type === 'current_count' || elem.type === 'start_count') {
                const checkErrorType = elem.type === 'current_count' ? item.error_reason_current_count : item.error_reason_start_count;
                const checkValue = checkTableCount(elem.value, checkErrorType);

                return (
                  <td key={index} className="refill-count">
                    {checkValue.tooltip ? (
                      <Tooltip placement="top" tooltipClass="tooltip__top" text={checkValue.tooltipText}>
                        <span className={`${checkValue.icon} refill-count__tooltip`} />
                      </Tooltip>
                    ) : (
                      <span>{checkValue.value}</span>
                    )}
                  </td>
                );
              }

              if (elem.type === 'order_id' || elem.type === 'quantity' || elem.type === 'to_refill') {
                if (elem.type === 'order_id') {
                  return (
                    <td key={index}>
                      {fullNavbarRoutes.find((route) => route.link === 'orders' && route.react) ? (
                        <Link to={`/orders?query=${item.order_id}&search_type=1`} className="order__link">
                          {elem.value}
                        </Link>
                      ) : (
                        <a href={`/admin/orders?query=${item.order_id}&search_type=1`} className="order__link">
                          {elem.value}
                        </a>
                      )}
                    </td>
                  );
                }
                return <td key={index}>{elem.value}</td>;
              }

              if (elem.type === 'service') {
                return (
                  <td key={index} rowSpan="2" className="service__row">
                    {item.service_id !== 0 && <span className="service__id">{item.service_id}</span>}
                    {elem.value}
                  </td>
                );
              }

              return (
                <td key={index} rowSpan="2">
                  {elem.value}
                </td>
              );
            })}
          </tr>

          <tr className="refill-link">
            <td colSpan="5">
              <span>{item.link}</span>
              {isValidURL(item.link) ? (
                <a href={`${window.location.origin}/anon.ws?r=${item.link}`} className="link-to-refill" rel="noopener noreferrer" target="_blank">
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
              ) : null}
            </td>
          </tr>
        </Fragment>
      ))}
    </tbody>
  );
};

TableBody.propTypes = {
  isLoading: PropTypes.bool,
  tasks: PropTypes.array,
  values: PropTypes.array,
  onChangeCheck: PropTypes.func,
  handleSelect: PropTypes.func,
  access: PropTypes.object,
  config: PropTypes.object,
};

export default TableBody;
